import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminHelpBox } from "@/components/admin/AdminHelpBox";
import { useAuth } from "@/context/AuthContext";
import { Search, ArrowLeft, LayoutGrid, Flag, AlertTriangle, Gavel, Users, Ban, Gamepad2, Activity, DoorOpen, LifeBuoy, Award, Coins, Trophy, Wallet, ShieldCheck, Gift, PackageCheck, Target, Zap, CalendarRange, Crown, HandCoins, Box, HeartHandshake, Dice5, RotateCw, Ticket, Sparkle, Sparkles, CloudRain, Flame, Megaphone, Home, Image, Clapperboard, Lightbulb, Bot, Scale, Send, HeartPulse, MessagesSquare } from "lucide-react";

const OWNER_ONLY = ["admins", "adminlog"];
const GROUPS = [
  ["Admin-Team (nur du)", OWNER_ONLY],
  ["Kontostände", ["richest", "compensations"]],
  ["Community & Moderation", ["reports", "team", "mugs", "secretchat", "warnings", "bans", "users", "exclusions", "gameblocks", "activity", "help", "honor", "farewells"]],
  ["Punkte & Prämien", ["settings", "pointsbalance", "audit", "rewards", "redemptions", "missions", "tasks", "challenges"]],
  ["Unterstützer & Spenden", ["supporters", "donations", "mystery", "support"]],
  ["Spiele & Aktionen", ["drops", "levelup", "games", "megaheart", "wheel", "lotto", "scratch", "goldrain", "sky", "quest"]],
  ["Inhalte & Einstellungen", ["whatsapp", "overview", "announcements", "landing", "promo", "promovideos", "ideas", "ai", "legal"]],
];
const ICONS = { admins: Crown, adminlog: ShieldCheck,  levelup: Award, whatsapp: MessagesSquare, secretchat: MessagesSquare, megaheart: HeartPulse, reports: Flag, warnings: AlertTriangle, bans: Gavel, users: Users, exclusions: Ban, gameblocks: Gamepad2, activity: Activity, help: LifeBuoy, honor: Award, farewells: DoorOpen, settings: Coins, richest: Trophy, compensations: HandCoins, pointsbalance: Wallet, audit: ShieldCheck, rewards: Gift, redemptions: PackageCheck, missions: Target, tasks: Zap, challenges: CalendarRange, supporters: Crown, donations: HandCoins, mystery: Box, support: HeartHandshake, games: Gamepad2, wheel: RotateCw, lotto: Dice5, scratch: Ticket, goldrain: CloudRain, contest: Sparkle, sky: Sparkles, quest: Flame, overview: Send, announcements: Megaphone, landing: Home, promo: Image, promovideos: Clapperboard, ideas: Lightbulb, ai: Bot, legal: Scale };

const useGrouped = (tabs, q) => useMemo(() => {
  const by = Object.fromEntries(tabs.map(([k, l, C]) => [k, { k, l, C }]));
  const s = q.trim().toLowerCase();
  const out = GROUPS.map(([g, keys]) => [g, keys.map((k) => by[k]).filter((t) => t && (!s || t.l.toLowerCase().includes(s) || g.toLowerCase().includes(s)))]);
  const known = new Set(GROUPS.flatMap(([, k]) => k));
  const rest = tabs.filter(([k]) => !known.has(k)).map(([k]) => by[k]).filter((t) => !s || t.l.toLowerCase().includes(s));
  return [...out, ["Weitere", rest]].filter(([, items]) => items.length);
}, [tabs, q]);

const SearchBox = ({ q, setQ, testId }) => (
  <label className="relative block">
    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Funktion suchen …" data-testid={testId}
      className="w-full h-11 pl-11 pr-4 rounded-full border border-pink-100 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-200" />
  </label>
);

const Hub = ({ groups, open, q, setQ }) => (
  <div className="space-y-6" data-testid="admin-hub">
    <SearchBox q={q} setQ={setQ} testId="admin-hub-search" />
    {groups.length === 0 && <p className="text-sm text-slate-400 text-center py-8">Keine Funktion gefunden.</p>}
    {groups.map(([g, items]) => (
      <section key={g} className="space-y-2.5">
        <h2 className="text-xs font-extrabold uppercase tracking-[0.18em] text-rose-500">{g}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5">
          {items.map((t) => { const I = ICONS[t.k] || LayoutGrid; return (
            <button key={t.k} onClick={() => open(t.k)} data-testid={`admin-tab-${t.k}`}
              className="group flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-3 p-3 rounded-2xl bg-white border border-pink-100 text-center sm:text-left hover:border-rose-300 hover:-translate-y-0.5 transition-[transform,border-color] min-w-0">
              <span className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center shrink-0 group-hover:bg-rose-500 group-hover:text-white transition-colors"><I className="w-5 h-5" /></span>
              <span className="text-[13px] sm:text-sm font-semibold leading-tight min-w-0 break-words hyphens-auto" lang="de">{t.l}</span>
            </button>
          ); })}
        </div>
      </section>
    ))}
  </div>
);

const Sidebar = ({ groups, active, open, q, setQ }) => (
  <aside className="hidden lg:block sticky top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto space-y-4 pr-1 no-scrollbar" data-testid="admin-sidebar">
    <button onClick={() => open(null)} data-testid="admin-nav-home" className={`w-full flex items-center gap-2 px-3 h-10 rounded-xl text-sm font-bold ${!active ? "bg-rose-500 text-white" : "hover:bg-rose-50"}`}><LayoutGrid className="w-4 h-4" /> Übersicht</button>
    <SearchBox q={q} setQ={setQ} testId="admin-nav-search" />
    {groups.map(([g, items]) => (
      <div key={g} className="space-y-0.5">
        <p className="px-3 text-[10px] font-extrabold uppercase tracking-[0.16em] text-slate-400">{g}</p>
        {items.map((t) => { const I = ICONS[t.k] || LayoutGrid; return (
          <button key={t.k} onClick={() => open(t.k)} data-testid={`admin-nav-${t.k}`}
            className={`w-full flex items-center gap-2.5 px-3 h-9 rounded-xl text-sm text-left transition-colors ${active === t.k ? "bg-rose-500 text-white font-bold" : "text-slate-600 hover:bg-rose-50"}`}><I className="w-4 h-4 shrink-0" /><span className="truncate">{t.l}</span></button>
        ); })}
      </div>
    ))}
  </aside>
);

export function AdminShell({ tabs: allTabs }) {
  const { user } = useAuth();
  const owner = !!user?.is_owner;
  const tabs = owner ? allTabs : allTabs.filter(([k]) => !OWNER_ONLY.includes(k));
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const groups = useGrouped(tabs, q);
  const active = tabs.find(([k]) => k === params.get("tab"));
  const open = (k) => { setParams(k ? { tab: k } : {}); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const C = active?.[2];
  return (
    <div className="lg:grid lg:grid-cols-[15rem_1fr] gap-6 items-start" data-testid="admin-shell">
      <Sidebar groups={groups} active={active?.[0]} open={open} q={q} setQ={setQ} />
      <div className="min-w-0">
        {active ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 sticky top-16 lg:static z-20 -mx-4 px-4 py-2 lg:m-0 lg:p-0 bg-white/85 backdrop-blur lg:bg-transparent">
              <button onClick={() => open(null)} data-testid="admin-back" className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-rose-50 text-rose-600 text-sm font-bold"><ArrowLeft className="w-4 h-4" /> Übersicht</button>
              <h2 className="text-lg sm:text-xl font-extrabold truncate" data-testid="admin-section-title">{active[1]}</h2>
            </div>
            <AdminHelpBox key={active[0]} tab={active[0]} owner={owner} />
            <div className="min-w-0 overflow-x-auto"><C /></div>
          </div>
        ) : <Hub groups={groups} open={open} q={q} setQ={setQ} />}
      </div>
    </div>
  );
}
